// src/js/core/db.js
//
// IndexedDB の薄いPromiseラッパ。00_情報設計_全体アーキテクチャ.md 5.1節の状態一覧のうち、
// サーバーを持たない・端末ローカルで完結させる状態（視聴位置、リアクションのオフライン
// キャッシュ、ダウンロード、オンボーディング進捗、文言キャッシュ）を保存する。
//
// プライベートブラウジングでは indexedDB 自体が使えない・容量が極端に小さいブラウザが
// あるため、開けない・書き込めない場合は例外を投げず「メモリのみで動作継続」に
// 自動的に降格する（IMPLEMENTATION.md 10章「空状態・エラー状態の分岐を実装」の一環）。

const DB_NAME = 'xte';
// v2: identity.js が使う `identities` ストアを追加した。
// v1 で作られた既存DBには onupgradeneeded で不足ストアだけが追加され、
// 既存ストア（watchedVideos の視聴位置等）はそのまま残る（下の実装を参照）。
const DB_VERSION = 2;

/**
 * ストア定義。IMPLEMENTATION.md の指定通り複数形の名前にする。
 * keyPath はストアごとの主キー項目名。
 */
const STORE_DEFS = [
  { name: 'watchedVideos', keyPath: 'videoId' },
  { name: 'reactions', keyPath: 'id' }, // id = `${objectType}:${objectId}` の合成キー
  { name: 'downloads', keyPath: 'videoId' },
  { name: 'onboarding', keyPath: 'key' },
  { name: 'copyCache', keyPath: 'key' },
  // identity.js の3層保持（Cookie / localStorage / IndexedDB）の最終層。
  // レコード形状は { key: 'current', token: string, updatedAt: number }（31_匿名識別とセッション.md §2.4）。
  { name: 'identities', keyPath: 'key' },
];

const STORE_NAMES = new Set(STORE_DEFS.map((def) => def.name));

/** @type {Promise<IDBDatabase|null>|null} */
let dbPromise = null;

/** ストアごとのメモリフォールバック（IndexedDB自体が使えない/途中で使えなくなった場合用）。 */
const memoryStores = new Map(STORE_DEFS.map((def) => [def.name, new Map()]));

/** 容量超過等で個別ストアがメモリ動作へ降格したかどうか。 */
const degradedStores = new Set();

function assertStoreName(storeName) {
  if (!STORE_NAMES.has(storeName)) {
    throw new Error(`[db] 未定義のストア名です: ${storeName}`);
  }
}

function keyPathOf(storeName) {
  return STORE_DEFS.find((def) => def.name === storeName).keyPath;
}

/**
 * DB接続を1回だけ開く（以後は同じPromiseを共有する）。
 * 開けなかった場合（プライベートブラウジング、非対応環境等）は null を解決し、
 * 呼び出し側は以後すべてメモリフォールバックへ回る。
 */
function getDb() {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolvePromise) => {
    if (typeof indexedDB === 'undefined') {
      resolvePromise(null);
      return;
    }

    let request;
    try {
      request = indexedDB.open(DB_NAME, DB_VERSION);
    } catch {
      resolvePromise(null);
      return;
    }

    request.onupgradeneeded = () => {
      const db = request.result;
      // バージョン移行: 既存ストアはそのまま残し、未作成のストアだけ追加する。
      // ここで deleteObjectStore()・clear() を呼んではならない。既存ユーザーの
      // 視聴位置（watchedVideos）等が消える。DB_VERSION を上げる際も、この
      // 「不足分のみ createObjectStore する」形を必ず維持すること。
      for (const def of STORE_DEFS) {
        if (!db.objectStoreNames.contains(def.name)) {
          db.createObjectStore(def.name, { keyPath: def.keyPath });
        }
      }
    };

    request.onsuccess = () => {
      const db = request.result;
      // 他タブでのバージョン変更などによる予期しない切断時もメモリへ降格させる。
      db.onversionchange = () => {
        db.close();
        dbPromise = null;
      };
      resolvePromise(db);
    };

    request.onerror = () => resolvePromise(null);
    request.onblocked = () => resolvePromise(null);
  });

  return dbPromise;
}

function readMemory(storeName, key) {
  return memoryStores.get(storeName).get(key);
}

function writeMemory(storeName, value) {
  const kp = keyPathOf(storeName);
  const key = value[kp];
  memoryStores.get(storeName).set(key, value);
  return key;
}

function deleteMemory(storeName, key) {
  memoryStores.get(storeName).delete(key);
}

function degrade(storeName, reason) {
  if (degradedStores.has(storeName)) return;
  degradedStores.add(storeName);
  console.warn(`[db] ストア "${storeName}" をメモリのみの動作に切り替えました: ${reason}`);
}

/**
 * 1件書き込む（新規・上書き両対応）。
 * @param {string} storeName
 * @param {object} value keyPath に対応するプロパティを含むオブジェクト
 * @returns {Promise<IDBValidKey>}
 */
export async function dbPut(storeName, value) {
  assertStoreName(storeName);

  if (degradedStores.has(storeName)) {
    return writeMemory(storeName, value);
  }

  const db = await getDb();
  if (!db) {
    degrade(storeName, 'IndexedDB を開けませんでした');
    return writeMemory(storeName, value);
  }

  return new Promise((resolve) => {
    try {
      const tx = db.transaction(storeName, 'readwrite');
      const req = tx.objectStore(storeName).put(value);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => {
        // QuotaExceededError 等。以後このストアはメモリ動作にフォールバックし、
        // 書き込み自体は失わずに継続させる。
        degrade(storeName, req.error?.name ?? '不明なエラー');
        resolve(writeMemory(storeName, value));
      };
    } catch (err) {
      degrade(storeName, err?.message ?? '不明な例外');
      resolve(writeMemory(storeName, value));
    }
  });
}

/**
 * 1件取得する。
 * @param {string} storeName @param {IDBValidKey} key
 * @returns {Promise<object|undefined>}
 */
export async function dbGet(storeName, key) {
  assertStoreName(storeName);

  if (degradedStores.has(storeName)) {
    return readMemory(storeName, key);
  }

  const db = await getDb();
  if (!db) {
    degrade(storeName, 'IndexedDB を開けませんでした');
    return readMemory(storeName, key);
  }

  return new Promise((resolve) => {
    try {
      const tx = db.transaction(storeName, 'readonly');
      const req = tx.objectStore(storeName).get(key);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(readMemory(storeName, key));
    } catch {
      resolve(readMemory(storeName, key));
    }
  });
}

/**
 * ストア全件を取得する。
 * @param {string} storeName
 * @returns {Promise<object[]>}
 */
export async function dbGetAll(storeName) {
  assertStoreName(storeName);

  if (degradedStores.has(storeName)) {
    return Array.from(memoryStores.get(storeName).values());
  }

  const db = await getDb();
  if (!db) {
    degrade(storeName, 'IndexedDB を開けませんでした');
    return Array.from(memoryStores.get(storeName).values());
  }

  return new Promise((resolve) => {
    try {
      const tx = db.transaction(storeName, 'readonly');
      const req = tx.objectStore(storeName).getAll();
      req.onsuccess = () => resolve(req.result ?? []);
      req.onerror = () => resolve(Array.from(memoryStores.get(storeName).values()));
    } catch {
      resolve(Array.from(memoryStores.get(storeName).values()));
    }
  });
}

/**
 * 1件削除する。
 * @param {string} storeName @param {IDBValidKey} key
 * @returns {Promise<void>}
 */
export async function dbDelete(storeName, key) {
  assertStoreName(storeName);

  if (degradedStores.has(storeName)) {
    deleteMemory(storeName, key);
    return;
  }

  const db = await getDb();
  if (!db) {
    degrade(storeName, 'IndexedDB を開けませんでした');
    deleteMemory(storeName, key);
    return;
  }

  await new Promise((resolve) => {
    try {
      const tx = db.transaction(storeName, 'readwrite');
      const req = tx.objectStore(storeName).delete(key);
      req.onsuccess = () => resolve();
      req.onerror = () => {
        deleteMemory(storeName, key);
        resolve();
      };
    } catch {
      deleteMemory(storeName, key);
      resolve();
    }
  });
}

/**
 * ストアを空にする。
 * @param {string} storeName
 * @returns {Promise<void>}
 */
export async function dbClear(storeName) {
  assertStoreName(storeName);
  memoryStores.get(storeName).clear();

  const db = await getDb();
  if (!db || degradedStores.has(storeName)) return;

  await new Promise((resolve) => {
    try {
      const tx = db.transaction(storeName, 'readwrite');
      const req = tx.objectStore(storeName).clear();
      req.onsuccess = () => resolve();
      req.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}

/** 現在メモリのみで動作しているストア名の一覧（診断・RUM送出用）。 */
export function getDegradedStoreNames() {
  return Array.from(degradedStores);
}

/**
 * store.js の永続化アダプタとして使える形に変換するヘルパ。
 * createStore() の persistence オプションに { type: 'custom', read, write } を
 * 渡す際に利用する。read は同期APIが要求される store.js の初期化タイミングでは
 * 使えないため、非同期プライミング（load）→ 通常の同期 get/set という2段構成にする。
 *
 * @param {string} storeName @param {IDBValidKey} key
 */
export function createDbBackedRecord(storeName, key) {
  let cache;
  return {
    /** store.js 初期化前に1回呼び、メモリキャッシュへ読み込んでおく。 */
    async load() {
      cache = await dbGet(storeName, key);
      return cache;
    },
    read() {
      return cache;
    },
    write(value) {
      cache = value;
      // 書き込み自体は非同期でよい（5秒間隔やpagehide等、呼び出し側の頻度制御に従う。
      // 00_情報設計_全体アーキテクチャ.md 3.2節）。失敗しても cache は更新済みなので
      // 表示上の一貫性は保たれる。
      dbPut(storeName, { ...value, [keyPathOf(storeName)]: key }).catch(() => {});
    },
  };
}
